import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useI18n } from '../../i18n/I18nContext';

/**
 * PageHead — the one page header every route starts with (spec §3.2). It renders the markup the
 * `.page-head` rules in index.css style, so a page stops hand-writing the same eight elements.
 *
 *   // a top-level page: title, one line under it, at most one yellow primary on the right;
 *   // on phones the nav icon becomes a 48px chip and the whole header one card
 *   <PageHead icon={<Users />} title="O'quvchilar" description="Progressni kuzating"
 *             actions={<button className="ds-btn ds-btn-primary">Qo'shish</button>} />
 *
 *   // a detail page: a 44px round back button, the parent's name above the title
 *   <PageHead back={{ to: '/students' }} eyebrow="O'quvchilar" title={student.name} />
 *   <PageHead back={{ onClick: goBack, label: 'Kursga qaytish' }} eyebrow="Kurs tahlili" title="O'quvchi progressi" />
 *
 * Props: title (the route's one <h1>), description, icon (an element), actions (a node), back
 * ({ to } for a link or { onClick } for a button, plus an optional `label` for screen readers;
 * default "Orqaga"), eyebrow (the parent's name — shown ONLY with `back`: top-level pages carry no
 * eyebrow, it would repeat the nav group), className.
 */
function PageHead({ title, description, icon, actions, back, eyebrow, className = '' }) {
  const { t } = useI18n();
  const cls = `page-head${back ? ' page-head--back' : ''}${className ? ` ${className}` : ''}`;

  const text = (
    <div className="page-head-text">
      {back && eyebrow && <p className="page-eyebrow">{eyebrow}</p>}
      <h1 className="page-title">{title}</h1>
      {description && <p className="page-sub">{description}</p>}
    </div>
  );

  if (back) {
    const label = back.label || t('ui.back');
    const common = {
      className: 'ds-btn ds-btn-secondary ds-btn-icon page-back',
      'aria-label': label,
      title: label,
    };
    return (
      <header className={cls}>
        {back.to ? (
          <Link to={back.to} {...common}><ArrowLeft aria-hidden="true" /></Link>
        ) : (
          <button type="button" onClick={back.onClick} {...common}><ArrowLeft aria-hidden="true" /></button>
        )}
        {text}
        {actions && <div className="page-actions">{actions}</div>}
      </header>
    );
  }

  return (
    <header className={cls}>
      <div className="page-head-main">
        {icon && <span className="page-head-icon" aria-hidden="true">{icon}</span>}
        {text}
      </div>
      {actions && <div className="page-actions">{actions}</div>}
    </header>
  );
}

export default PageHead;
